import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';

import {AppendDirection} from '../flowchartManagementUtils';
import {ProcedureEditorContext} from '../ProcedureEditorContext';
import {
  ADD_NODE_BUTTON_WIDTH,
  COMMON_NODE_WIDTH,
  DASHED_LINE_LENGTH,
  makeOpacityStyle,
} from '../procedureMethodNodeCommonConsts';
import {ProcedureMethodAddNodeButton} from './ProcedureMethodAddNodeButton';

export enum NodeLocationType {
  ROOT,
  LEAF,
}

const DASHED_LINE_TOP_PIXELS = 24;
const ROOT_DASHED_LINE_LEFT_PIXELS = -DASHED_LINE_LENGTH;
const LEAF_DASHED_LINE_LEFT_PIXELS = COMMON_NODE_WIDTH + 1;

const BUTTON_TOP_PIXELS = 16;
const ROOT_BUTTON_LEFT_PIXELS = -DASHED_LINE_LENGTH - ADD_NODE_BUTTON_WIDTH + 1;
const LEAF_BUTTON_LEFT_PIXELS = COMMON_NODE_WIDTH + DASHED_LINE_LENGTH + 1;

export function ProcedureEndsMethodNodeAddNodeButton(props: {
  methodCid: string;
  nodeLocationType: NodeLocationType;
  onClick: (nodeId: string, appendDirection?: AppendDirection) => void;
}): JSX.Element {
  const {methodCidBeingDragged} = ProcedureEditorContext.useContext();
  const isRoot = props.nodeLocationType === NodeLocationType.ROOT;
  const appendDirection = isRoot ? AppendDirection.UPSTREAM : AppendDirection.DOWNSTREAM;
  const buttonLeftPixels = isRoot ? ROOT_BUTTON_LEFT_PIXELS : LEAF_BUTTON_LEFT_PIXELS;
  const hasValidDropZone = methodCidBeingDragged !== props.methodCid;

  return (
    // Reactflow relies on 'nodrag nopan' class to prevent elements from dragging a node.
    // Ref: https://reactflow.dev/docs/api/nodes/custom-nodes/#prevent-dragging--selecting
    <div
      className={classNames(
        'nodrag nopan',
        css(styles.endAddMethodButton, !hasValidDropZone && makeOpacityStyle(methodCidBeingDragged))
      )}
      {...getTestProps({
        component: 'ProcedureEndsMethodNodeAddNodeButton',
        key: isRoot ? 'root' : 'leaf',
      })}
      data-amp-safe={false}
    >
      <ProcedureMethodAddNodeButton
        top={BUTTON_TOP_PIXELS}
        left={buttonLeftPixels}
        hasValidDropZone={hasValidDropZone}
        onClick={() => {
          props.onClick(props.methodCid, appendDirection);
        }}
      />
      <svg
        width={`${DASHED_LINE_LENGTH}`}
        height="2"
        viewBox="0 0 42 2"
        fill="none"
        className={css(
          styles.dashedLine,
          isRoot ? styles.rootDashedLine : styles.leafDashedLine,
          makeOpacityStyle(methodCidBeingDragged)
        )}
      >
        <path d="M0 1L100 1" stroke={GRAY_DARK} strokeDasharray="2 2" strokeWidth="2px" />
      </svg>
    </div>
  );
}

const styles = StyleSheet.create({
  endAddMethodButton: {
    position: 'absolute',
    fontSize: 12,
    pointerEvents: 'all',
  },
  dashedLine: {
    position: 'absolute',
    top: `${DASHED_LINE_TOP_PIXELS}px`,
  },
  rootDashedLine: {
    left: `${ROOT_DASHED_LINE_LEFT_PIXELS}px`,
  },
  leafDashedLine: {
    left: `${LEAF_DASHED_LINE_LEFT_PIXELS}px`,
  },
});
